import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { useEffect, useRef, useState } from 'react';
import { ReactJewishDatePicker, type BasicJewishDay } from 'react-jewish-datepicker';
import 'react-jewish-datepicker/dist/index.css';
import { Checkbox } from '../Checkbox';
import type { FieldAppearance } from '../fieldAppearance';
import { SmPickerField } from '../shared/SmPickerField';
import { HEBREW_PICKER_STYLES } from './HebrewDatePicker.theme';
import { gregorianFromJewishDate, gregorianToBasicJewishDay } from './utils';

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected || typeof document === 'undefined') return;
  const el = document.createElement('style');
  el.setAttribute('data-sm', 'hebrew-date-picker');
  el.textContent = HEBREW_PICKER_STYLES;
  document.head.appendChild(el);
  stylesInjected = true;
}

export type HebrewDatePickerProps = {
  label?: string;
  /** @default 'default' */
  appearance?: FieldAppearance;
  placeholder?: string;
  /** @default true */
  fullWidth?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  /** Hebrew date display string (e.g. from API). */
  hebrewDate: string;
  /** Gregorian `YYYY-MM-DD` — anchors the calendar when Hebrew text is empty. */
  gregorianDate?: string;
  onHebrewDateChange: (hebrew: string) => void;
  onGregorianDateChange?: (gregorian: string) => void;
  /** Fired when a day is chosen in the calendar (for legacy `setBasicJewishDay` consumers). */
  onJewishDaySelect?: (day: BasicJewishDay) => void;
  showAfterSunset?: boolean;
  afterSunset?: boolean;
  onAfterSunsetChange?: (checked: boolean) => void;
  checkboxAlignment?: 'flex-start' | 'center' | 'flex-end';
  checkboxPosition?: 'top' | 'bottom';
};

/** Hebrew calendar field using design-system field chrome ({@link SmPickerField}). */
export function HebrewDatePicker({
  label,
  appearance = 'default',
  placeholder,
  fullWidth = true,
  disabled,
  error,
  helperText,
  hebrewDate,
  gregorianDate,
  onHebrewDateChange,
  onGregorianDateChange,
  onJewishDaySelect,
  showAfterSunset = false,
  afterSunset = false,
  onAfterSunsetChange,
  checkboxAlignment = 'center',
  checkboxPosition = 'top'
}: HebrewDatePickerProps) {
  injectStyles();
  const [displayHebrew, setDisplayHebrew] = useState(hebrewDate);
  const [calendarDate, setCalendarDate] = useState<Date | null>(
    () => gregorianToBasicJewishDay(gregorianDate)?.date ?? null
  );
  const [isAfterSunset, setIsAfterSunset] = useState(afterSunset);
  const triggerRef = useRef<HTMLDivElement>(null);

  const resolvedAfterSunset = afterSunset !== undefined ? afterSunset : isAfterSunset;

  useEffect(() => {
    setDisplayHebrew(hebrewDate);
  }, [hebrewDate]);

  useEffect(() => {
    if (afterSunset !== undefined) setIsAfterSunset(afterSunset);
  }, [afterSunset]);

  useEffect(() => {
    const normalized = gregorianDate?.trim() ?? '';
    const hasHebrew = Boolean(hebrewDate?.trim());
    if (!normalized && !hasHebrew) { setCalendarDate(null); return; }
    const jewishDay = gregorianToBasicJewishDay(normalized);
    if (jewishDay) {
      setCalendarDate(jewishDay.date);
      if (!hasHebrew && jewishDay.jewishDateStrHebrew) setDisplayHebrew(jewishDay.jewishDateStrHebrew);
    }
  }, [gregorianDate, hebrewDate]);

  const emitGregorian = (date: Date) =>
    onGregorianDateChange?.(gregorianFromJewishDate(date, resolvedAfterSunset));

  const handleDaySelect = (day: BasicJewishDay) => {
    setDisplayHebrew(day.jewishDateStrHebrew);
    setCalendarDate(day.date);
    onHebrewDateChange(day.jewishDateStrHebrew);
    emitGregorian(day.date);
    onJewishDaySelect?.(day);
  };

  const handleAfterSunsetChange = (checked: boolean) => {
    setIsAfterSunset(checked);
    onAfterSunsetChange?.(checked);
    if (calendarDate) emitGregorian(calendarDate);
  };

  const checkboxNode = showAfterSunset ? (
    <Box sx={{
      position: 'absolute', right: 0,
      display: 'flex', alignItems: 'center', justifyContent: checkboxAlignment,
      mb: checkboxPosition === 'top' ? 0.5 : 0, mt: checkboxPosition === 'bottom' ? 0.5 : 0, gap: 0.5
    }}>
      <Checkbox checked={resolvedAfterSunset} onChange={e => handleAfterSunsetChange(e.target.checked)}
        size="small" density="compact" label="אחרי השקיעה" disabled={disabled} />
    </Box>
  ) : null;

  return (
    <Box sx={{ position: 'relative' }}>
      {checkboxPosition === 'top' && checkboxNode}
      <Box sx={{ position: 'relative' }}>
        {/* Hidden picker root spans the whole field width so popup aligns under input */}
        <Box
          ref={triggerRef}
          sx={{
            position: 'absolute',
            insetInlineEnd: 0,
            top: 0,
            width: '100%',
            height: 0,
            opacity: 1,
            pointerEvents: 'auto',
            overflow: 'visible'
          }}
        >
          <ReactJewishDatePicker
            className="jewish-picker"
            value={calendarDate ?? undefined}
            isHebrew
            onClick={handleDaySelect}
          />
        </Box>

        <SmPickerField
          label={label}
          appearance={appearance}
          placeholder={placeholder}
          fullWidth={fullWidth}
          disabled={disabled}
          error={error}
          helperText={helperText}
          value={displayHebrew}
          onChange={e => {
            const next = e.target.value;
            setDisplayHebrew(next);
            onHebrewDateChange(next);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" className="SmInputAdornment-root">
                <CalendarTodayOutlinedIcon
                  onClick={() => {
                    if (disabled) return;
                    triggerRef.current?.querySelector<HTMLElement>('.selectedDate')?.click();
                  }}
                  sx={{ fontSize: 20, cursor: disabled ? 'default' : 'pointer' }}
                />
              </InputAdornment>
            )
          }}
        />
      </Box>
      {checkboxPosition === 'bottom' && checkboxNode}
    </Box>
  );
}
